import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";
import Map, {
  Marker,
  NavigationControl,
  type MapRef,
  type MapLayerMouseEvent,
} from "react-map-gl/maplibre";
import type { NewRestroom, RestroomFilters } from "../types";
import { createRestroom } from "../lib/api";
import { nearestRestroom } from "../lib/geo";
import { useRestroomsInView } from "../lib/useRestroomsInView";
import { useGeolocation } from "../lib/useGeolocation";
import { useRestroomSelection } from "../lib/useRestroomSelection";
import { countryByCode, detectCountry, storeCountry, type CountryCode } from "../lib/countries";
import { useT } from "../i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import CountryPicker from "./CountryPicker";
import AddRestroomForm from "./AddRestroomForm";
import ClusteredRestroomMarkers from "./ClusteredRestroomMarkers";
import RestroomDetail from "./RestroomDetail";
import MeasurementNotice from "./MeasurementNotice";
import mapWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";

const MAP_STYLE =
  (import.meta.env.VITE_MAP_STYLE_URL as string) ||
  "https://tiles.openfreemap.org/styles/liberty";
const INITIAL_COUNTRY = countryByCode(detectCountry());

const NO_FILTERS: RestroomFilters = { wheelchair: false, free: false, changingTable: false };

export default function RestroomMap() {
  const t = useT();
  const mapRef = useRef<MapRef>(null);
  const [map, setMap] = useState<MapRef | null>(null);
  const [filters, setFilters] = useState<RestroomFilters>(NO_FILTERS);
  const { restrooms, truncated, bounds, zoom, syncView, loadForBounds, addRestroom } =
    useRestroomsInView(mapRef, filters);
  const { userPos, locating, locate } = useGeolocation(mapRef);
  const { selected, highlightId, copied, select, close, share, openDeepLink } =
    useRestroomSelection(mapRef);

  const [addMode, setAddMode] = useState(false);
  const [draft, setDraft] = useState<{ lat: number; lng: number } | null>(null);
  const [country, setCountry] = useState<CountryCode>(INITIAL_COUNTRY.code);

  const attachMap = useCallback((instance: MapRef | null) => {
    mapRef.current = instance;
    setMap(instance);
  }, []);

  // MapRef exists before styles/tiles finish loading. Fetch our own data then,
  // with current filters/locale, without reinitializing on later UI changes.
  const initializeMap = useEffectEvent(() => {
    syncView();
    loadForBounds();
    openDeepLink();
  });

  useEffect(() => {
    if (map) initializeMap();
  }, [map]);

  const handleMapClick = useCallback(
    (ev: MapLayerMouseEvent) => {
      if (!addMode) return;
      setDraft({ lat: ev.lngLat.lat, lng: ev.lngLat.lng });
    },
    [addMode],
  );

  const flyTo = useCallback((lng: number, lat: number, z: number) => {
    mapRef.current?.flyTo({ center: [lng, lat], zoom: z });
  }, []);

  // Přepnutí země: ulož volbu a přeleť na její střed.
  const changeCountry = useCallback(
    (code: CountryCode) => {
      setCountry(code);
      storeCountry(code);
      const c = countryByCode(code);
      flyTo(c.center.lng, c.center.lat, c.zoom);
    },
    [flyTo],
  );

  const handleCreate = useCallback(
    async (data: NewRestroom, turnstileToken: string) => {
      const created = await createRestroom(data, turnstileToken);
      addRestroom(created);
      setDraft(null);
      setAddMode(false);
    },
    [addRestroom],
  );

  // Geolokace: po nalezení polohy vybrat nejbližší viditelné WC.
  const handleLocate = useCallback(() => {
    locate((pos) => {
      const nearest = nearestRestroom(pos, restrooms);
      if (nearest) select(nearest);
    });
  }, [locate, restrooms, select]);

  const toggle = (key: keyof RestroomFilters) =>
    setFilters((f) => ({ ...f, [key]: !f[key] }));

  return (
    <div className="map-wrap">
      <header className="topbar">
        <strong>🚽 Khakham</strong>
        <div className="topbar-actions">
          <button className="btn" onClick={handleLocate} disabled={locating}>
            {locating ? t("topbar.locating") : t("topbar.locate")}
          </button>
          <button
            className={addMode ? "btn active" : "btn"}
            onClick={() => {
              setAddMode((v) => !v);
              setDraft(null);
            }}
          >
            {addMode ? t("topbar.addCancel") : t("topbar.add")}
          </button>
          <CountryPicker value={country} onChange={changeCountry} />
          <LanguageSwitcher />
        </div>
      </header>

      <div className="filterbar">
        <button className={filters.wheelchair ? "chip active" : "chip"} onClick={() => toggle("wheelchair")}>
          {t("filters.wheelchair")}
        </button>
        <button className={filters.free ? "chip active" : "chip"} onClick={() => toggle("free")}>
          {t("filters.free")}
        </button>
        <button className={filters.changingTable ? "chip active" : "chip"} onClick={() => toggle("changingTable")}>
          {t("filters.changingTable")}
        </button>
        <span className="count">
          {truncated
            ? t("filters.countTruncated", { n: restrooms.length })
            : t("filters.count", { n: restrooms.length })}
        </span>
      </div>

      {addMode && !draft && <div className="hint">{t("map.addHint")}</div>}

      <Map
        ref={attachMap}
        // Bundle the worker and its imports; MapLibre's runtime URL is not discoverable by Vite.
        workerUrl={mapWorkerUrl}
        mapStyle={MAP_STYLE}
        initialViewState={{
          longitude: INITIAL_COUNTRY.center.lng,
          latitude: INITIAL_COUNTRY.center.lat,
          zoom: INITIAL_COUNTRY.zoom,
        }}
        style={{ width: "100%", height: "100%" }}
        onMoveEnd={() => {
          syncView();
          loadForBounds();
        }}
        onClick={handleMapClick}
      >
        <NavigationControl position="bottom-right" />

        <ClusteredRestroomMarkers
          restrooms={restrooms}
          bounds={bounds}
          zoom={zoom}
          highlightId={highlightId}
          onSelect={select}
          onClusterClick={flyTo}
        />

        {userPos && (
          <Marker longitude={userPos.lng} latitude={userPos.lat} anchor="center">
            <span className="user-dot" title={t("geo.userPos")} />
          </Marker>
        )}

        {draft && (
          <Marker longitude={draft.lng} latitude={draft.lat} anchor="bottom">
            <span className="pin draft">📍</span>
          </Marker>
        )}
      </Map>

      <MeasurementNotice />

      {selected && (
        <RestroomDetail
          restroom={selected}
          userPos={userPos}
          copied={copied}
          onShare={share}
          onClose={close}
        />
      )}

      {draft && (
        <AddRestroomForm
          lat={draft.lat}
          lng={draft.lng}
          onSubmit={handleCreate}
          onCancel={() => setDraft(null)}
        />
      )}
    </div>
  );
}
